import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  CardHeader,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import BaseWrapper from "../../components/shared/BaseWrapper";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import GetAppIcon from "@mui/icons-material/GetApp";
import boazLogo from "./img/image.png";
import jsPDF from "jspdf";
import { formatDateInPhilippines } from "./utils/phDate";

const formatDisplayDateTime = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if(isNaN(d.getTime())) return value;
  const [date] = value.split('T');

  const time = d.toLocaleTimeString('en-PH', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone:'Asia/Manila'
  });

  return `${date} ${time}`;
};

const getUserDisplayName = (user) => {
  if (!user) return "";
  if (typeof user === "string") {
    return user.toUpperCase() === "UNKNOWN" ? "" : user;
  }
  const cleanUserValue = (value) => {
    if (!value) return "";
    const text = String(value).trim();
    return text.toUpperCase() === "UNKNOWN" ? "" : text;
  };
  if (user.name && typeof user.name === "object") return getUserDisplayName(user.name);
  const name = cleanUserValue(user.name);
  if (name) return name;
  const firstName = user.firstname || user.first_name || user.given_name || "";
  const lastName = user.lastname || user.last_name || user.family_name || "";
  return [firstName, lastName].filter(Boolean).join(" ") ||
    cleanUserValue(user.username) ||
    cleanUserValue(user.user_id) ||
    cleanUserValue(user.updated_by) ||
    cleanUserValue(user.email);
};

const getCurrentUserDisplayName = () => {
  try {
    const stored = localStorage.getItem("user");
    if (!stored) return "";
    return getUserDisplayName(JSON.parse(stored));
  } catch {
    return "";
  }
};

const getInvoiceUpdatedByDisplayName = (invoice) =>
  getUserDisplayName(invoice?.user_update) ||
  getUserDisplayName(invoice?.user_name) ||
  getUserDisplayName(invoice?.user_id) ||
  getUserDisplayName(invoice?.updated_by) ||
  getUserDisplayName(invoice?.user_updated);

function ViewInvoice() {
  const CURRENCY = 'PHP';
  const darkBlue = [9, 35, 56];
  const lightBlue = [12, 63, 96];
  const lightTextColor = [236, 240, 241];
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const salesReportInvoiceKey = searchParams.get("salesReportInvoice");
  const salesReportState = useMemo(() => {
    if (!salesReportInvoiceKey) return null;

    try {
      const stored = localStorage.getItem(salesReportInvoiceKey);
      if (!stored) return null;
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }, [salesReportInvoiceKey]);

  const invoice = location.state?.invoice || salesReportState?.invoice;
  const selectedClient = location.state?.selectedClient || salesReportState?.selectedClient;
  const selectedContract = location.state?.selectedContract || salesReportState?.selectedContract;
  const source = location.state?.source || salesReportState?.source || 'manageContracts';
  const contractSource = location.state?.contractSource || salesReportState?.contractSource
  const contractId = selectedContract?.contract_id || selectedContract?.id;

  const [ loading, setLoading ] = useState(false);
  const currentUserDisplayName = getCurrentUserDisplayName();
  const createdByDisplayName = getUserDisplayName(invoice?.user_create);
  const updatedByDisplayName = getInvoiceUpdatedByDisplayName(invoice) || currentUserDisplayName;
  const isPaidInvoice = invoice?.status === 'PAID';

  const backRoute = 
    source === 'manageClients' ? '/manage-clients' : 
    source === 'viewContract' ? `/view-contract/${contractId}` : 
    source === 'salesReport' ? '/sales-report' :
    '/manage-contracts';
  
  const backState = useMemo(() =>
    source === 'viewContract'
      ? { contract: selectedContract, selectedClient, source: contractSource } 
      :  { selectedClient },
    [source, selectedContract, selectedClient, contractSource]
  );

  useEffect(() => {
    if (!invoice) {
      navigate(backRoute, {
        state: backState
      });
    }
  }, [invoice, navigate, backRoute, backState]);

  const handleBackClick = () => {
    navigate(backRoute, {
      state: backState 
    });
  };

  const handleEditClick = () => {
    navigate('/edit-invoice', {
      state: {
        invoice,
        selectedClient,
        selectedContract,
        source,
        contractSource,
        returnTo: 'viewInvoice',
      },
    });
  };

  // PDF export
  const handleDownloadPDF = () => {
    try {
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const PW = doc.internal.pageSize.getWidth();
      const PH = doc.internal.pageSize.getHeight();
      const M = 7;
      const R = PW - M;
      const LH = 3.5;
      let y = M+5;

      const safe = (v) => (v == null ? "" : String(v));
      const normalizeLines = (value) => safe(value)
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n");
      const htmlToPdfLines = (html) => {
        const source = safe(html)
          .replace(/<br\s*\/?>/gi, "\n")
          .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
          .replace(/<li[^>]*>/gi, "- ")
          .replace(/&nbsp;/gi, " ")
          .replace(/&amp;/gi, "&")
          .replace(/&lt;/gi, "<")
          .replace(/&gt;/gi, ">")
          .replace(/&quot;/gi, '"')
          .replace(/&#39;/gi, "'")
          .replace(/<[^>]+>/g, "");

        return source
          .replace(/\n{3,}/g, "\n\n")
          .split("\n")
          .map((line) => line.trimEnd());
      };
      const wrapPreservedLines = (value, width, isHtml = false) => {
        const lines = isHtml ? htmlToPdfLines(value) : normalizeLines(value);
        return lines.flatMap((line) => {
          if (!line.trim()) return [""];
          return wrap(line, width);
        });
      };
      const getDescriptionBlocks = (html) => {
        const root = document.createElement("div");
        root.innerHTML = safe(html);
        const blocks = [[]];
        const currentBlock = () => blocks[blocks.length - 1];
        const addBlock = () => {
          if (currentBlock().length) blocks.push([]);
        };
        const addText = (text, style) => {
          const normalized = text.replace(/\s+/g, " ");
          if (normalized) currentBlock().push({ text: normalized, ...style });
        };
        const walk = (node, style = {}) => {
          if (node.nodeType === Node.TEXT_NODE) {
            addText(node.textContent || "", style);
            return;
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          const tag = node.tagName.toLowerCase();
          if (tag === "br") {
            blocks.push([]);
            return;
          }

          const nextStyle = {
            bold: style.bold || tag === "strong" || tag === "b",
            italic: style.italic || tag === "em" || tag === "i",
            underline: style.underline || tag === "u",
          };

          if (tag === "li") {
            addText("- ", nextStyle);
          }

          Array.from(node.childNodes).forEach((child) => walk(child, nextStyle));

          if (["p", "div", "li"].includes(tag)) {
            addBlock();
          }
        };

        Array.from(root.childNodes).forEach((child) => walk(child));
        return blocks.filter((block, index) => block.length || index === 0);
      };
      const renderDescription = (html, x, startY, maxWidth) => {
        const lineHeight = 3.2;
        let cursorX = x;
        let cursorY = startY;
        const ensureLine = () => {
          if (cursorY > PH - M - 15) {
            doc.addPage();
            cursorY = M;
          }
        };

        getDescriptionBlocks(html).forEach((block) => {
          ensureLine();
          cursorX = x;

          if (!block.length) {
            cursorY += lineHeight;
            return;
          }

          block.forEach((segment) => {
            const tokens = segment.text.split(/(\s+)/).filter(Boolean);
            tokens.forEach((token) => {
              const tokenWidth = doc.getTextWidth(token);
              if (token.trim() && cursorX + tokenWidth > x + maxWidth) {
                cursorY += lineHeight;
                cursorX = x;
                ensureLine();
              }

              const fontStyle = segment.bold && segment.italic
                ? "bolditalic"
                : segment.bold
                  ? "bold"
                  : segment.italic
                    ? "italic"
                    : "normal";
              doc.setFont("helvetica", fontStyle);
              doc.text(token, cursorX, cursorY);
              if (segment.underline && token.trim()) {
                doc.setLineWidth(0.1);
                doc.line(cursorX, cursorY + 0.5, cursorX + tokenWidth, cursorY + 0.5);
              }
              cursorX += tokenWidth;
            });
          });

          cursorY += lineHeight;
        });

        doc.setFont("helvetica", "normal");
        return cursorY;
      };
      const money = (minor) => {
        const n = Number(minor || 0) / 100;
        return `${CURRENCY} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
      const moneyNum = (minor) => {
        const n = Number(minor || 0) / 100;
        return `${CURRENCY} ${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      };
      const fmtDate = (s) => {
        if (!s) return "N/A";
        const d = new Date(s);
        return isNaN(d) ? safe(s) : d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
      };
      const wrap = (t, w) => doc.splitTextToSize(safe(t), w);
      const need = (n) => { if (y + n > PH - M - 15) { doc.addPage(); y = M; } };
      const breakLine = (color = [ 180, 180, 180 ], lw = 0.3) => {
        doc.setDrawColor(...color); doc.setLineWidth(lw);
        doc.line(M, y, R, y);
      };

      const invoiceNumber = invoice?.invoice_number ?? invoice?.invoice_number ?? "";
      const taxMinor = invoice?.tax_minor ?? 0;
      const totalMinor = invoice?.total_due_minor ?? 0;
      const preparedByName = updatedByDisplayName || createdByDisplayName;
      const taxRatePct = invoice?.tax_rate_bps != null
        ? (Number(invoice.tax_rate_bps) / 100).toFixed(2) : "12.00";
        const netOfTax = Math.max(0, Number(totalMinor) - Number(taxMinor));

      //BOAZ LOGO
      const logoWidth = 30;
      const logoProps = doc.getImageProperties(boazLogo);
      const logoHeight = logoWidth * (logoProps.height / logoProps.width);
      doc.addImage(boazLogo, "PNG", M+2, y-2, logoWidth, logoHeight);

      //COMPANY INFORMATION
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const companyLines = [
        "Boaz Pest Management Corp.",
        "P. Victor St.",
        "Makati, Philippines 1212",
        "9932508753",
        "finance.boazpestctrl@gmail.com",
        "www.boazpestcontrol.com",
        "Govt. UID TIN: 601-617-459-000",
      ];
      const companyStartY = y;
      doc.text(companyLines[0], M+38, companyStartY);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      for(let i=1; i < companyLines.length; i++){
        const t = companyLines[i];
        doc.text(t, M+38, companyStartY + i * LH);
      }

      //BILLING INVOICE LABEL
      doc.setTextColor(...darkBlue);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text('Billing Invoice', R-47, y+2, { align: 'left' });

      //INVOICE NUMBER LABEL
      doc.setTextColor(...darkBlue);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(safe(invoiceNumber), R-47, y+8, { align: 'left' });
      doc.setTextColor(0, 0, 0);

      y = companyStartY + companyLines.length * LH + 15;

      breakLine(darkBlue, 0.5); y += 3;

      // BILL TO + DATE / PLEASE PAY / DUE DATE
      const dateBoxesWidth = (R - M) * 0.5;
      const rectW = dateBoxesWidth / 3;
      const rectH = 25;
      const billToBoxGap = 4;
      const c1 = M;
      const c2 = R - dateBoxesWidth;
      const c3 = c2 + rectW;
      const c4 = c3 + rectW;
      const centerBoxText = (text, x, textY, options = {}) => {
        doc.text(text, x + rectW / 2, textY, { align: "center", ...options });
      };

      //BILL TO
      const clientName = invoice.client?.name || selectedClient?.name || "";
      const clientAddress = invoice.client?.billing_address || selectedClient?.billing_address || "";
      const billToWidth = c2 - c1 - billToBoxGap;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      const billLines = [
        ...wrapPreservedLines(clientName, billToWidth),
        ...wrapPreservedLines(clientAddress, billToWidth),
      ].filter((line, index, lines) => line || index < lines.length - 1);
      let valY = y + LH;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text('BILL TO', c1, y);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      billLines.forEach((t, i) => doc.text(t, c1, valY + i * LH));

      const clientDetailsHeight = valY + billLines.length * LH;

      // BILLING DATE
      valY = y+8.5;
      doc.setFillColor(...lightTextColor);
      doc.rect(c2, y-2, rectW, rectH, "F");

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...lightBlue);
      centerBoxText('DATE', c2, valY);
      centerBoxText(fmtDate(invoice?.billing_date), c2, valY + LH);

      //PLEASE PAY
      doc.setFillColor(...lightBlue);
      doc.rect(c3, y-2, rectW, rectH, "F");

      //doc.setFont('helvetica', 'normal');
      doc.setTextColor(...lightTextColor);
      centerBoxText('PLEASE PAY', c3, valY);

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      centerBoxText(money(totalMinor), c3, valY + LH);

      //DUE DATE
      doc.setFillColor(200, 200, 200);
      doc.rect(c4, y-2, rectW, rectH, "F");

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(...lightBlue);
      centerBoxText('DUE DATE', c4, valY);
      centerBoxText(fmtDate(invoice?.due_date), c4, valY + LH);

      y += rectH-2;
      doc.setDrawColor(...darkBlue);
      doc.setLineWidth(0.3);
      doc.line(c2, y, c4+rectW, y);

      y = Math.max(clientDetailsHeight, y) + 7;
      valY = y + LH;
      
      //PREPARED BY
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text('PREPARED BY', c1, y);
      doc.setFont('helvetica', 'normal');
      doc.text(safe(preparedByName || "N/A"), c1, valY);
      
      // VERIFIED BY
      doc.setFont('helvetica', 'bold');
      doc.text('VERIFIED BY', c1+95, y);
      doc.setFont('helvetica', 'normal');
      doc.text(safe("EJ Marinay"), c1 + 95, valY);
      
      y += 10;
      breakLine([200, 200, 200], 0.2);
      y += LH;

      const items = Array.isArray(invoice?.line_items) ? invoice.line_items : [];
      // LINE ITEM HEADERS
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...darkBlue);
      const dateColX = c1;
      const descColX = c1 + 30;
      const amountColX = R;
      doc.text('DATE', dateColX, y);
      doc.text('DESCRIPTION', descColX, y);
      doc.text('AMOUNT', R - 3, y, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      y += 2;
      breakLine([200, 200, 200], 0.2); y += LH;

      if (!items.length) {
        doc.setFont('helvetica', 'normal');
        doc.text('No line items', c1, y);
        y += 6;
      } else {
        items.forEach((item) => {
          need(18);
          const itemDate = item?.sales_date || item?.date || item?.service_date || "";
          const rowStartY = y;
          doc.setFont('helvetica', 'bold');
          doc.text(itemDate ? fmtDate(itemDate) : "", dateColX, y);
          const itemLabel = item?.category || item?.type || "";
          if (itemLabel) {
            doc.text(safe(itemLabel), descColX, y);
            y += LH;
          }

          doc.setFont('helvetica', 'normal');
          y = renderDescription(item?.description || "", descColX, y, amountColX - descColX - 12);

          const amt = item?.amount_minor != null
            ? money(item.amount_minor)
            : `${CURRENCY}${Number(item?.amount || 0).toFixed(2)}`;
          doc.text(amt, R, rowStartY, { align: "right" });
          breakLine([200, 200, 200], 0.2); y += LH;
        });
      }
      y += LH;

      // ── TWO COLUMNS: payment instructions (left) | totals (right) ──
      need(65);
      const instrWidth = 95;
      const totColX = R - 80;
      const sectionStartY = y;

      // LEFT: payment instructions
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      const instrText = [
        "Thank you for your business! Please make cheques payable to: Boaz Pest Management Corp.",
        "",
        "For your online and over-the-counter payments, please deposit in any of the following and send your deposit slip or confirmation to our email. Thank you!",
        "",
        "Security Bank",
        "Account Name: Boaz Pest Management Corp.",
        "Current Account: 00000-5115-8251",
        "",
        "BDO",
        "Account Name: Boaz Pest Management Corp.",
        "Savings account: 007010091479",
        "",
        "GCASH - Eric John Marinay",
        "0956-906-4433",
        "",
        "Credit card payment: Log to our online payment site -",
        "https://paymongo.page/l/boazpestcontrol",
      ];
      let instrY = sectionStartY;
      instrText.forEach((t) => {
        if (t === "") { instrY += 3; return; }
        wrap(t, instrWidth).forEach((ln) => { doc.text(ln, c1, instrY); instrY += 4; });
      });

      // RIGHT: totals
      let totY = sectionStartY;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...lightBlue);
      doc.text('INCLUDES TAX', totColX, totY);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(moneyNum(taxMinor), R, totY, { align: 'right' });
      totY += LH;

      doc.setFontSize(9);
      doc.setTextColor(...lightBlue);
      doc.text('TOTAL', totColX, totY);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(moneyNum(totalMinor), R, totY, { align: "right" });
      totY += 7;

      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.2);
      doc.line(totColX - 2, totY, R, totY);
      totY += LH;

      doc.setFontSize(9);
      doc.setTextColor(...lightBlue);
      doc.text('TOTAL DUE', totColX, totY);
      
      doc.setFontSize(14);
      doc.setTextColor(...darkBlue);

      doc.text(money(totalMinor), R, totY+1, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      totY += 5;

      doc.setDrawColor(...lightBlue);
      doc.setLineWidth(0.3);
      doc.line(totColX - 2, totY, R, totY);
      totY += 4;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...lightBlue);
      doc.text('THANK YOU.', R, totY, { align: 'right' });

      y = Math.max(instrY, totY) + 8;

      // ── RECEIVED BY ──────────────────────────────────────────
      need(18);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      doc.text('Received by:', c1, y);
      y += 8;
      doc.setDrawColor(100, 100, 100);
      doc.setLineWidth(0.3);
      doc.line(c1, y, c1 + 80, y);
      y += 4;
      doc.text('Signature over printed name / Date', c1, y);
      y += 10;

      // ── TAX SUMMARY ──────────────────────────────────────────
      need(22);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text('TAX SUMMARY', c1, y);
      y += 2;
      breakLine([200,200,200], 0.2); y += LH;

      const colDiv = (PW - M*2)/3;
      const x1 = colDiv+6;
      const x2 = x1 + colDiv;
      const x3 = x2 + colDiv;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text("RATE", x1, y, { align: 'right' });
      doc.text("TAX", x2, y, { align: 'right' });
      doc.text("NET", x3, y, { align: 'right' });
      y += LH;
      breakLine([200, 200, 200], 0.2); y += LH;

      doc.setFont('helvetica', 'normal');
      doc.text(`VAT @ ${taxRatePct}%`, x1, y, { align: 'right' });
      doc.text(moneyNum(taxMinor), x2, y, { align: 'right' });
      doc.text(moneyNum(netOfTax), x3, y, { align: 'right' });
      y += 10;

      // ── BOTTOM NOTE ──────────────────────────────────────────
      need(12);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const bottomNote = wrap("Please disregard this Billing Invoice if already paid. Thank you so much! For any questions, kindly call 0993-250-8753 and look for Ms. Jan Fatima Abad. www.boazpestcontrol.com", PW - M * 2);
      y = PH - M - (bottomNote.length * LH);
      bottomNote.forEach((t) => { doc.text(t, PW/2, y, { align: 'center'}); y += LH; });

      const filename = `Invoice_${safe(invoiceNumber) || "INV"}_${formatDateInPhilippines(new Date())}.pdf`;
      doc.save(filename);
    } catch (err) {
      console.error("PDF export error:", err);
    } finally {
      setLoading(false);
    }
  };

  const netTotal = invoice.net_total_minor / 100;
  const taxAmount = invoice.tax_minor / 100;
  const totalDue = invoice.total_due_minor / 100;
  const taxRate = (invoice.tax_rate_bps / 10000 * 100).toFixed(2);

  const getStatusColor = (status) => {
    switch (status) {
      case 'PAID':
        return '#155724';
      case 'OVERDUE':
        return '#721c24';
      case 'OPEN':
        return '#978d2d';
      default:
        return '#f5f5f5';
    }
  };

  const getStatusBgColor = (status) => {
    switch (status) {
      case 'PAID' : return '#d4edda';
      case 'OVERDUE' : return '#f8d7da';
      case 'OPEN' : return '#fff9c4';
      default: return '#f5f5f5';
    }
  }

  return (
    <BaseWrapper
      titleIcon={<ReceiptLongIcon />}
      title='Billing'
      subTitle='View Invoice'
      subTitleRoute='/view-invoice'
    >
      <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
        {/* Header with back and edit buttons */}
          <Stack direction='row' spacing={2} sx={{ mb: 3 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              variant='outlined'
            >
              Back
            </Button>
            <Button
              startIcon={<EditIcon />}
              onClick={handleEditClick}
              variant='contained'
              color='primary'
              disabled={loading}
            >
              Edit
            </Button>
            <Button
              startIcon={<GetAppIcon />}
              onClick={handleDownloadPDF}
              variant='contained'
              color='success'
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Download PDF'}
            </Button>
          </Stack>
          {/* Title + status */}
          <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant='h5' sx={{ fontWeight: 'bold' }}>
              Invoice {invoice.invoice_number}
            </Typography>
            <Chip
              label={invoice.status}
              sx={{
                backgroundColor: getStatusBgColor(invoice.status), 
                color: getStatusColor(invoice.status),
                fontWeight: 'bold',
              }}
            />
          </Box>
          
          {/* Invoice Details */}
          <Box sx={{ mb: 1 }}>
            <CardHeader
              title={
                <Typography variant='h6' sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '1rem', pb: '0.5rem'}}>
                  Invoice Details
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Box>
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color:'#666', mb: 0.5 }}>Invoice ID</Typography>
                <Typography variant='body1'>{invoice.invoice_id || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Invoice Number</Typography>
                <Typography variant='body1'>{invoice.invoice_number || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Contract ID</Typography>
                <Typography variant='body1'>{invoice.contract_id || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Billing Date</Typography>
                <Typography variant='body1'>{invoice.billing_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Due Date</Typography>
                <Typography variant='body1'>{invoice.due_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Status</Typography>
                <Chip
                  label={invoice.status}
                  size='small'
                  sx={{
                    backgroundColor: getStatusBgColor(invoice.status),
                    color: getStatusColor(invoice.status),
                    fontWeight: 'bold',
                  }}
                />
              </Grid> 
              {isPaidInvoice && (
                <>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Payment Date</Typography>
                    <Typography variant='body1'>{invoice.payment_date || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Payment Notes</Typography>
                    <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>{invoice.payment_notes || 'N/A'}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          {/* Client Info */}
          {(selectedClient || invoice.client) && (
            <>
              <Box sx={{ mb: 1 }}>
                <CardHeader
                  title={
                    <Typography variant='h6' sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '1rem', pb: '0.5rem' }}>
                      Client
                    </Typography>
                  }
                  sx={{ pb: 0, pt: 0 }}
                />
              </Box>
              <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Name</Typography>
                    <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                      {invoice.client?.name || selectedClient?.name || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Billing Address</Typography>
                    <Typography variant='body1' sx={{ whiteSpace: 'pre-wrap' }}>
                      {invoice.client?.billing_address || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </>
          )}

          {/* Line Items */}
          <Box sx={{ mb: 1 }}>
            <CardHeader
              title={
                <Typography variant='h6' sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '1rem', pb: '0.5rem' }}>
                  Line Items
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Box>
          <Paper elevation={0} sx={{ borderRadius: 2, mb: 3 }}>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoice.line_items?.length > 0 ? (
                    invoice.line_items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.sale_date || '—'}</TableCell>

                        <TableCell>
                          {item.description ? (
                            <Box
                              sx={{
                                "& p": { m: 0 },
                                "& strong": { fontWeight: "bold" },
                                "& em": { fontStyle: "italic" },
                                "& u": { textDecoration: "underline" },
                              }}
                              dangerouslySetInnerHTML={{ __html: item.description }}
                            />
                          ) : (
                            '—'
                          )}
                        </TableCell>

                        <TableCell>
                          {CURRENCY} {(item.amount_minor / 100).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align='center'>
                        <Typography color='text.secondary' variant='body2' sx={{ py: 2 }}>No line items</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {/* Financials */}
          <Box sx={{ mb: 1 }}>
            <CardHeader
              title={
                <Typography variant='h6' sx={{ fontWeight: 'bold', textTransform: 'uppercase', fonSize: '1rem', pb: '0.5rem' }}>
                  Financials
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            /> 
          </Box>
          <Paper elevation={0} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Net Total</Typography>
                <Typography variant='body1'>{CURRENCY} {netTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Tax Rate</Typography>
                <Typography variant='body1'>{taxRate}%</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Tax Amount</Typography>
                <Typography variant='body1'>{CURRENCY} {taxAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Total Due</Typography>
                <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#1976d2' }}>{CURRENCY} {totalDue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
              </Grid>
            </Grid>
          </Paper>
          {/* Metadata */}
          <Box sx={{ mb: 1 }}>
            <CardHeader
              title={
                <Typography variant='h6' sx={{ fontWeight: 'bold', textTransform: 'uppercase', fontSize: '1rem', pb: '0.5rem' }}>
                  Metadata
                </Typography>
              }
              sx={{ pb: 0, pt: 0 }}
            />
          </Box>
          <Paper elevation={1} sx={{ p: 3, backgroundColor: '#f9f9f9', borderRadius: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Created On</Typography>
                <Typography variant='body2'>{formatDisplayDateTime(invoice.timestamp_create)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Updated On</Typography>
                <Typography variant='body2'>{formatDisplayDateTime(invoice.timestamp_update)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Created By</Typography>
                <Typography variant='body2'>{createdByDisplayName || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant='subtitle2' sx={{ fontWeight: 'bold', color: '#666', mb: 0.5 }}>Updated By</Typography>
                <Typography variant='body2'>{updatedByDisplayName || 'N/A'}</Typography>
              </Grid>
            </Grid>
          </Paper>
      </Box>
    </BaseWrapper>
  );
}

export default ViewInvoice;
